import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  cancelMailScheduledSend,
  fetchMailScheduledSends,
  processMailScheduledDue,
  sendMailScheduledNow,
  type MailScheduledSendEntry,
} from "@/lib/api/mailScheduledSend";
import {
  applyScheduledSendsFromServer,
  getScheduledSendsCache,
  setScheduledSendsCache,
  subscribeScheduledSendsCacheChanged,
  upsertScheduledSendCache,
} from "@/lib/outbox/scheduledSendsSessionCache";
import {
  completeMailActivity,
  failMailActivity,
  mailActivitySendLabel,
  startMailActivity,
} from "@/lib/mail/mailActivityJournalNote";
import type { MailMessageSummary } from "@/lib/types/mail";

/** Filet si l’onglet est throttlé / cron en retard. */
const SCHEDULED_DUE_POLL_MS = 30_000;
/** Léger slack après l’heure pile (horloge + latence). */
const SCHEDULED_WAKE_SLACK_MS = 400;

type Options = {
  accountId?: number;
  /** Recharge complète à l’ouverture de la vue outbox. */
  enabled?: boolean;
  /** Poll MySQL tant qu’il reste des envois pending (même hors vue outbox). */
  watchDue?: boolean;
};

function isPendingEntry(entry: MailScheduledSendEntry): boolean {
  return entry.status === "pending";
}

function entrySendAtMs(entry: MailScheduledSendEntry): number {
  return new Date(entry.sendAt).getTime();
}

function isDueEntry(entry: MailScheduledSendEntry, now = Date.now()): boolean {
  if (!isPendingEntry(entry)) return false;
  if (entry.id < 1) return false; // placeholder local (programmation en cours)
  const t = entrySendAtMs(entry);
  return Number.isFinite(t) && t <= now;
}

function scheduledSendLabel(prefix: string, entry?: MailScheduledSendEntry | null) {
  return mailActivitySendLabel({
    prefix,
    subject: entry?.subject,
    to: entry?.payload?.to ?? entry?.payload?.toRaw,
    cc: entry?.payload?.cc,
  });
}

/** Prochaine échéance future ; si déjà dû → maintenant (déclenche un tick immédiat). */
function nextScheduledWakeMs(entries: MailScheduledSendEntry[]): number | null {
  const now = Date.now();
  let minFuture: number | null = null;
  let hasDue = false;
  for (const entry of entries) {
    if (!isPendingEntry(entry)) continue;
    if (entry.id < 1) continue; // placeholder local — pas une vraie échéance serveur
    const t = entrySendAtMs(entry);
    if (!Number.isFinite(t)) continue;
    if (t <= now) {
      hasDue = true;
      continue;
    }
    if (minFuture == null || t < minFuture) minFuture = t;
  }
  if (hasDue) return now;
  return minFuture;
}

function entriesForAccount(
  entries: MailScheduledSendEntry[],
  accountId: number | undefined
): MailScheduledSendEntry[] {
  if (accountId == null) return [];
  return entries.filter((entry) => entry.accountId === accountId);
}

export function scheduledSendsToSummaries(
  entries: MailScheduledSendEntry[],
  accountEmail: string,
  sendingIds?: ReadonlySet<number>
): MailMessageSummary[] {
  return entries.map((entry, index) => {
    const due = isDueEntry(entry);
    const sending = entry.id < 1 || sendingIds?.has(entry.id) === true || due;
    return {
      uid: -(10000 + index + 1),
      scheduledSendId: entry.id,
      subject: entry.subject.trim() || "(Sans objet)",
      from: accountEmail,
      date: new Date(entry.sendAt).toUTCString(),
      seen: true,
      flagged: false,
      hasAttachments: false,
      size: 0,
      sendStatus: sending ? "sending" : "scheduled",
      sendScheduledAt: entry.sendAt,
      sendTo: entry.payload?.toRaw ?? entry.payload?.to ?? "",
    };
  });
}

export function useMailScheduledSends({
  accountId,
  enabled = false,
  watchDue = false,
}: Options) {
  const [entries, setEntries] = useState<MailScheduledSendEntry[]>(() =>
    accountId != null ? (getScheduledSendsCache(accountId) ?? []) : []
  );
  const [loading, setLoading] = useState(false);
  const [sendingIds, setSendingIds] = useState<number[]>([]);
  const accountIdRef = useRef(accountId);
  accountIdRef.current = accountId;
  const reloadRef = useRef<() => Promise<void>>(async () => {});
  const tickInFlightRef = useRef(false);
  const entriesRef = useRef(entries);
  entriesRef.current = entries;

  const reload = useCallback(async () => {
    if (accountIdRef.current == null) {
      setEntries([]);
      setSendingIds([]);
      return;
    }

    const currentAccountId = accountIdRef.current;
    const hadCache = getScheduledSendsCache(currentAccountId) != null;
    if (!hadCache) setLoading(true);

    const snapshotBeforeFetch = getScheduledSendsCache(currentAccountId) ?? [];

    try {
      const res = await fetchMailScheduledSends({ accountId: currentAccountId });
      if (res.ok && accountIdRef.current === currentAccountId) {
        const next = applyScheduledSendsFromServer(
          currentAccountId,
          res.data.scheduled ?? [],
          snapshotBeforeFetch
        );
        setEntries(next);
        const pendingIds = new Set(
          next.filter(isPendingEntry).map((entry) => entry.id)
        );
        setSendingIds((prev) => prev.filter((id) => pendingIds.has(id)));
      } else if (!res.ok && accountIdRef.current === currentAccountId) {
        const cached = getScheduledSendsCache(currentAccountId);
        if (cached) setEntries(cached);
      }
    } finally {
      if (accountIdRef.current === currentAccountId) {
        setLoading(false);
      }
    }
  }, []);

  reloadRef.current = reload;

  useEffect(() => {
    if (accountId == null) {
      setEntries([]);
      setSendingIds([]);
      return;
    }
    setEntries(getScheduledSendsCache(accountId) ?? []);
  }, [accountId]);

  useEffect(() => {
    return subscribeScheduledSendsCacheChanged(() => {
      const currentAccountId = accountIdRef.current;
      if (currentAccountId == null) return;
      setEntries(getScheduledSendsCache(currentAccountId) ?? []);
    });
  }, []);

  useEffect(() => {
    if (!enabled || accountId == null) return;

    const cached = getScheduledSendsCache(accountId);
    if (cached) setEntries(cached);
    // Ne pas recharger tant qu’un placeholder local tourne (sinon liste figée / spinner mort).
    if (cached?.some((entry) => entry.id < 1)) return;
    void reload();
  }, [accountId, enabled, reload]);

  /** Sync initiale / changement de compte — hors tick due (évite d’écraser le spinner). */
  useEffect(() => {
    if (!watchDue || accountId == null) return;
    const cached = getScheduledSendsCache(accountId);
    if (cached?.some((entry) => entry.id < 1)) return;
    void reload();
  }, [accountId, reload, watchDue]);

  const accountEntries = useMemo(
    () => entriesForAccount(entries, accountId),
    [accountId, entries]
  );

  const pendingEntries = useMemo(
    () => accountEntries.filter(isPendingEntry),
    [accountEntries]
  );
  const pendingCount = pendingEntries.length;
  const hasPending = pendingCount > 0;
  const nextWakeAt = useMemo(() => nextScheduledWakeMs(pendingEntries), [pendingEntries]);
  const sendingIdSet = useMemo(() => new Set(sendingIds), [sendingIds]);
  const sendingCount = useMemo(() => {
    const now = Date.now();
    let n = 0;
    for (const entry of pendingEntries) {
      if (entry.id < 1 || sendingIdSet.has(entry.id) || isDueEntry(entry, now)) n += 1;
    }
    return n;
  }, [pendingEntries, sendingIdSet]);

  useEffect(() => {
    if (!watchDue || accountId == null) return;

    const cached = getScheduledSendsCache(accountId);
    if (cached) {
      setEntries(cached);
    }

    let cancelled = false;

    const tick = async () => {
      const currentAccountId = accountIdRef.current;
      if (currentAccountId == null || cancelled || tickInFlightRef.current) return;

      const list =
        entriesRef.current.length > 0
          ? entriesRef.current
          : (getScheduledSendsCache(currentAccountId) ?? []);
      const now = Date.now();
      const dueIds = list
        .filter((entry) => isPendingEntry(entry) && entry.id > 0)
        .filter((entry) => {
          const t = entrySendAtMs(entry);
          return Number.isFinite(t) && t <= now + SCHEDULED_WAKE_SLACK_MS;
        })
        .map((entry) => entry.id);

      if (dueIds.length === 0) return;

      // Spinner immédiat.
      setSendingIds((prev) => [...new Set([...prev, ...dueIds])]);

      const dueEntries = list.filter((entry) => dueIds.includes(entry.id));
      const journalId = startMailActivity(
        dueIds.length === 1
          ? scheduledSendLabel("Envoi programmé", dueEntries[0])
          : `Envoi de ${dueIds.length} messages programmés…`,
        "send",
        "local"
      );

      tickInFlightRef.current = true;
      try {
        const res = await processMailScheduledDue({ accountId: currentAccountId });
        // Retrait optimiste dès SMTP OK — ne pas attendre un reload lent / file IMAP.
        if (res.ok && (res.data.sent > 0 || res.data.processed > 0)) {
          setEntries((prev) => {
            const next = prev.filter((entry) => !dueIds.includes(entry.id));
            setScheduledSendsCache(currentAccountId, next);
            return next;
          });
          setSendingIds((prev) => prev.filter((id) => !dueIds.includes(id)));
          completeMailActivity(
            journalId,
            dueIds.length === 1
              ? scheduledSendLabel("Envoyé (programmé)", dueEntries[0])
              : `${dueIds.length} envois programmés terminés`
          );
        } else if (!res.ok) {
          failMailActivity(journalId, res.message);
        } else {
          completeMailActivity(journalId, "Envoi programmé — rien à envoyer");
        }
        if (!cancelled) {
          void reloadRef.current();
        }
      } catch (err) {
        failMailActivity(
          journalId,
          err instanceof Error ? err.message : "Échec envoi programmé"
        );
        if (!cancelled) {
          await reloadRef.current();
        }
      } finally {
        tickInFlightRef.current = false;
      }
    };

    void tick();

    if (!hasPending && nextWakeAt == null) {
      return () => {
        cancelled = true;
      };
    }

    const intervalId = window.setInterval(() => {
      void tick();
    }, SCHEDULED_DUE_POLL_MS);

    let wakeTimerId: number | undefined;
    let wakeRetryId: number | undefined;
    if (nextWakeAt != null) {
      const delay = Math.max(SCHEDULED_WAKE_SLACK_MS, nextWakeAt - Date.now() + SCHEDULED_WAKE_SLACK_MS);
      wakeTimerId = window.setTimeout(() => {
        void tick();
        wakeRetryId = window.setTimeout(() => {
          void tick();
        }, 750);
      }, delay);
    }

    const onVisible = () => {
      if (document.visibilityState !== "visible") return;
      void tick();
    };
    document.addEventListener("visibilitychange", onVisible);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      if (wakeTimerId != null) window.clearTimeout(wakeTimerId);
      if (wakeRetryId != null) window.clearTimeout(wakeRetryId);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [accountId, hasPending, nextWakeAt, watchDue]);

  const upsert = useCallback(
    (entry: MailScheduledSendEntry) => {
      if (accountId == null) return;
      upsertScheduledSendCache(accountId, entry);
      setEntries(getScheduledSendsCache(accountId) ?? []);
    },
    [accountId]
  );

  const cancel = useCallback(
    async (id: number) => {
      if (accountId == null) return false;
      const res = await cancelMailScheduledSend({ accountId, id });
      if (res.ok) {
        setEntries((prev) => {
          const next = prev.filter((entry) => entry.id !== id);
          setScheduledSendsCache(accountId, next);
          return next;
        });
        setSendingIds((prev) => prev.filter((sendingId) => sendingId !== id));
        return true;
      }
      window.alert(res.message);
      return false;
    },
    [accountId]
  );

  const sendNow = useCallback(
    async (id: number) => {
      if (accountId == null) return false;
      const entry = accountEntries.find((row) => row.id === id) ?? null;
      const journalId = startMailActivity(
        scheduledSendLabel("Envoi programmé", entry),
        "send",
        "local"
      );
      setSendingIds((prev) => [...new Set([...prev, id])]);
      setEntries((prev) => {
        const next = prev.filter((row) => row.id !== id);
        setScheduledSendsCache(accountId, next);
        return next;
      });
      const res = await sendMailScheduledNow({ accountId, id });
      if (!res.ok) {
        failMailActivity(journalId, res.message);
        window.alert(res.message);
        await reload();
        return false;
      }
      completeMailActivity(
        journalId,
        scheduledSendLabel("Envoyé (programmé)", entry)
      );
      await reload();
      return true;
    },
    [accountEntries, accountId, reload]
  );

  const count = pendingCount;

  const toSummaries = useCallback(
    (accountEmail: string) =>
      scheduledSendsToSummaries(accountEntries, accountEmail, sendingIdSet),
    [accountEntries, sendingIdSet]
  );

  const getById = useCallback(
    (id: number) => accountEntries.find((entry) => entry.id === id) ?? null,
    [accountEntries]
  );

  return useMemo(
    () => ({
      entries: accountEntries,
      count,
      sendingCount,
      loading,
      reload,
      upsert,
      cancel,
      sendNow,
      toSummaries,
      getById,
    }),
    [
      cancel,
      count,
      accountEntries,
      getById,
      loading,
      reload,
      sendNow,
      sendingCount,
      toSummaries,
      upsert,
    ]
  );
}

export { upsertScheduledSendCache };
